import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import {
  CheckCircle,
  NumberCircleOne,
  NumberCircleTwo,
  NumberCircleThree,
  NumberCircleFour
} from '@phosphor-icons/react';
import { useCreateJob } from '@/store/createJob';
import { Stepper } from '@/types/enums';
import { JSX } from 'react';

interface ProgressContent {
  type: 'step' | 'divider';
  status: 'active' | 'inactive' | 'completed';
  activeIcon: JSX.Element | null;
  inactiveIcon: JSX.Element | null;
  completedIcon: JSX.Element | null;
  text: string;
}

export default function CreateJobProgress({ isLoading }: { isLoading: boolean }) {
  const createJobStore = useCreateJob();

  const stepMap: Record<Stepper, number> = {
    [Stepper.Client]: 1,
    [Stepper.JobDetails]: 2,
    [Stepper.AiAnalysis]: 3,
    [Stepper.Review]: 4,
    [Stepper.Success]: 5
  };

  const getStatus = (step: Stepper): 'active' | 'inactive' | 'completed' => {
    if (isLoading) return 'inactive';
    const currentStep = stepMap[createJobStore.step];
    const targetStep = stepMap[step];
    return currentStep === targetStep
      ? 'active'
      : currentStep > targetStep
      ? 'completed'
      : 'inactive';
  };

  const progressSteps: Array<{
    step: Stepper;
    icon: JSX.Element;
    text: string;
  }> = [
    {
      step: Stepper.Client,
      icon: <NumberCircleOne size={32} />,
      text: 'Client'
    },
    {
      step: Stepper.JobDetails,
      icon: <NumberCircleTwo size={32} />,
      text: 'Job Details'
    },
    {
      step: Stepper.AiAnalysis,
      icon: <NumberCircleThree size={32} />,
      text: 'AI Analysis'
    },
    {
      step: Stepper.Review,
      icon: <NumberCircleFour size={32} />,
      text: 'Review'
    }
  ];

  const createProgressContent = (): ProgressContent[] => {
    return progressSteps.flatMap(({ step, icon, text }, index) => {
      const stepContent: ProgressContent = {
        type: 'step',
        status: getStatus(step),
        activeIcon: React.cloneElement(icon, {
          color: '#4E76FF',
          weight: 'fill'
        }),
        inactiveIcon: React.cloneElement(icon, {
          color: '#D5D5D5',
          weight: 'regular'
        }),
        completedIcon: <CheckCircle size={32} color="#4E76FF" />,
        text
      };

      const dividerContent: ProgressContent | null =
        index < progressSteps.length - 1
          ? {
              type: 'divider',
              status: getStatus(progressSteps[index + 1].step),
              activeIcon: null,
              inactiveIcon: null,
              completedIcon: null,
              text: ''
            }
          : null;

      return dividerContent ? [stepContent, dividerContent] : [stepContent];
    });
  };

  return (
    <Box as="nav" w="100%" mb="56px" display="flex" justifyContent="center">
      <Flex alignItems="center">
        {createProgressContent().map((content, index) => (
          <Flex key={index} flexDir="row" alignItems="center">
            {content.type === 'divider' ? (
              <Box
                w="120px"
                m="10px"
                borderBottom="3px solid"
                borderColor={content.status === 'active' ? 'primary.900' : 'neutrals.300'}
              />
            ) : (
              <>
                {content.status === 'active'
                  ? content.activeIcon
                  : content.status === 'completed'
                  ? content.completedIcon
                  : content.inactiveIcon}
                <Text
                  ml="5px"
                  fontSize="16px"
                  fontWeight={500}
                  color={isLoading ? 'neutrals.300' : 'neutrals.900'}
                >
                  {content.text}
                </Text>
              </>
            )}
          </Flex>
        ))}
      </Flex>
    </Box>
  );
}
